import { keys } from 'ramda'
import ApiClient from 'api-client/ApiClient'
import { getActiveTenantId } from 'app/core/utils/helpers'
import store from 'app/store'

export const getPhysicalNetworkRequestBody = (params) => {
  // Need to add tenant id so that system users can make the call too
  const currentTenantId = getActiveTenantId()
  if (params.networkType === 'flat') {
    return {
      network: {
        name: params.name,
        description: params.description,
        mtu: params.mtu,
        dns_domain: params.dns_domain,
        admin_state_up: params.adminState === 'Up',
        'provider:network_type': params.networkType,
        'provider:physical_network': params.networkLabel,
        'router:external': !!params.external,
        shared: !!params.shared,
        port_security_enabled: !!params.enablePortSecurity,
        tenant_id: currentTenantId,
      },
    }
  } else if (params.networkType === 'vlan') {
    return {
      network: {
        name: params.name,
        description: params.description,
        mtu: params.mtu,
        dns_domain: params.dns_domain,
        admin_state_up: params.adminState === 'Up',
        'provider:network_type': params.networkType,
        'provider:physical_network': params.networkLabel,
        'provider:segmentation_id': params.segmentationId,
        'router:external': !!params.external,
        shared: !!params.shared,
        port_security_enabled: !!params.enablePortSecurity,
        tenant_id: currentTenantId,
      },
    }
  } else if (params.networkType === 'vxlan') {
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState === 'Up',
        dns_domain: params.dns_domain,
        'provider:network_type': params.networkType,
        'provider:segmentation_id': params.segmentationId,
        'router:external': !!params.external,
        shared: !!params.shared,
        port_security_enabled: !!params.enablePortSecurity,
        tenant_id: currentTenantId,
      },
    }
  }
  return {}
}

export const getNetworkRequestBody = (params, hostConfigs) => {
  const currentTenantId = getActiveTenantId()
  const underlayNetworkType = store.getState()?.session?.underlayNetwork?.type || 'vlan'
  // If underlay network type is vlan, then all of the host configs
  // should have same underlay network label, just look at the first
  const firstHostConfig = hostConfigs?.[0]
  const tunnelPhysnet = firstHostConfig?.tunnelingInterface
  const labelKeys = keys(firstHostConfig?.networkLabels)
  const tunnelLabel = labelKeys?.find((key) => {
    return firstHostConfig?.networkLabels?.[key] === tunnelPhysnet
  })
  const underlayNetworkLabel = underlayNetworkType === 'vlan' ? tunnelLabel : ''
  if (params.networkType === 'tenant') {
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState !== 'Down',
        shared: params.shared,
        port_security_enabled: params.enablePortSecurity,
        dns_domain: params.dns_domain,
        mtu: underlayNetworkType === 'vlan' ? 1500 : underlayNetworkType === 'vxlan' ? 1450 : 1440, //https://platform9.atlassian.net/browse/PCD-3552
        tenant_id: currentTenantId,
        // Don't send these at all, we just have one underlay &
        // neutron will choose... in the future if we allow
        // more than one underlay this part may be needed
        // 'provider:network_type': underlayNetworkType,
        // // TODO - Remove the hardcoding for provider:physical_network
        // ...(underlayNetworkType === 'vlan' && {
        //   'provider:physical_network': underlayNetworkLabel,
        // }),
      },
    }
  } else if (params.networkType === 'provider') {
    // todo
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState !== 'Down',
        shared: params.shared,
        port_security_enabled: params.enablePortSecurity,
        dns_domain: params.dns_domain,
        mtu: underlayNetworkType === 'vlan' ? 1500 : 1450, //https://platform9.atlassian.net/browse/PCD-3552
        tenant_id: currentTenantId,
        // Don't send these at all, we just have one underlay &
        // neutron will choose... in the future if we allow
        // more than one underlay this part may be needed
        // 'provider:network_type': underlayNetworkType,
        // // TODO - Remove the hardcoding for provider:physical_network
        // ...(underlayNetworkType === 'vlan' && {
        //   'provider:physical_network': underlayNetworkLabel,
        // }),
      },
    }
  } else if (params.networkType === 'external') {
    return {
      network: {
        name: params.name,
        description: params.description,
        admin_state_up: params.adminState !== 'Down',
        shared: params.shared,
        port_security_enabled: params.enablePortSecurity,
        dns_domain: params.dns_domain,
        'provider:network_type': params.externalNetworkType,
        'provider:physical_network': 'external', // presumed value
        'provider:segmentation_id':
          params.externalNetworkType === 'vlan' ? params.segmentationId : undefined,
        'router:external': true,
        mtu: underlayNetworkType === 'vlan' ? 1500 : 1450, //https://platform9.atlassian.net/browse/PCD-3552
        tenant_id: currentTenantId,
      },
    }
  }
  return {}
}

export const getLoadBalancerRequestBody = (params) => {
  return {
    loadbalancer: {
      name: params?.name,
      vip_subnet_id: params?.vipSubnetId?.id,
      provider: params?.provider,
    },
  }
}

export const getListenerRequestBody = (params) => {
  return {
    listener: {
      name: params?.name,
      loadbalancer_id: params?.loadBalancer,
      protocol: params?.protocol,
      protocol_port: params?.port,
    },
  }
}

export const getPoolRequestBody = (params) => {
  return {
    pool: {
      name: params?.name,
      listener_id: params?.listener,
      protocol: params?.protocol,
      lb_algorithm: params?.loadBalancingAlgorithm,
    },
  }
}

export const getMemberRequestBody = (params) => {
  return {
    member: {
      name: params?.name,
      address: params?.ipAddress,
      protocol_port: params?.port,
      subnet_id: params?.vipSubnet?.id,
    },
  }
}

export const getHealthMonitorRequestBody = (params) => {
  return {
    healthmonitor: {
      type: params?.type,
      pool_id: params?.pool,
      delay: params?.delay,
      timeout: params?.timeout,
      max_retries: params?.retry,
    },
  }
}

/**
 * Build request body for Simple Network (L2-only) creation
 *
 * Simple Networks are similar to Managed Networks but without subnets.
 * Key differences:
 *  - No subnets (L2-only, IP configuration done in guest OS)
 *  - Port security always disabled
 *  - External network option not applicable
 *  - Tagged with "simple_network" tag after creation
 *
 * Similar fields to Managed Networks:
 *  - Network Label (provider:physical_network)
 *  - Network Type (provider:network_type)
 *  - VLAN ID (provider:segmentation_id) for VLAN type
 *  - Admin State, MTU, Shared
 *
 * Note: UI shows "Simple Network" in user-facing text
 * NOTE: After creation, must call addSimpleNetworkTag() to mark network
 */
export const getSimpleNetworkRequestBody = (params) => {
  // Need to add tenant id so that system users can make the call too
  const currentTenantId = getActiveTenantId()
  const base = {
    network: {
      name: params.name,
      description: params.description,
      mtu: params.mtu,
      admin_state_up: params.adminState === 'Up',
      'provider:network_type': params.networkType, // 'vlan', 'flat', or 'vxlan'
      'provider:physical_network': params.networkLabel, // Required for Simple Networks
      shared: !!params.shared, // Can be shared across tenants
      // Port security always disabled for Simple Networks
      port_security_enabled: false,
      // External network option not applicable for Simple Networks
      'router:external': false,
      tenant_id: currentTenantId,
    },
  }

  // Add VLAN ID only for VLAN type
  if (params.networkType === 'vlan') {
    base.network['provider:segmentation_id'] = Number(params.segmentationId)
  }

  return base
}

/**
 * Add "simple_network" tag to identify Simple Networks
 * Must be called after network creation
 *
 * @param networkId - The UUID of the created network
 * @returns Promise<void>
 *
 * API: PUT /v2.0/networks/{network_id}/tags
 * Reference: https://docs.openstack.org/api-ref/network/v2/index.html#create-tags
 */
export const addSimpleNetworkTag = async (networkId: string): Promise<void> => {
  const neutron = ApiClient.getInstance().neutron
  // PUT /v2.0/networks/{network_id}/tags with tag "simple_network"
  await neutron.addNetworkTags(networkId, ['simple_network'])
}

/**
 * Disables `dns_publish_fixed_ip` for subnets where it's currently enabled.
 */
export const updateSubnetsDnsPublishFlag = async (subnets = [], updateSubnetFn): Promise<void> => {
  if (!Array.isArray(subnets) || subnets.length === 0) return

  const subnetsToUpdate = subnets.filter((subnet) => subnet.dns_publish_fixed_ip)

  if (subnetsToUpdate.length === 0) return

  await Promise.all(
    subnetsToUpdate.map((subnet) =>
      updateSubnetFn({
        id: subnet.id,
        body: {
          subnet: {
            dns_publish_fixed_ip: false,
          },
        },
      }),
    ),
  )
}
